import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils';
import {
  ADVANCED_SEARCHES_TAG_TYPE,
  ALL_ADVANCED_SEARCHES_ID,
  searchResultsTagId,
} from './constants';
import type {
  AdvancedSearchListResponse,
  AdvancedSearchDetail,
  AdvancedSearchExecuteParams,
  AdvancedSearchExecuteResponse,
  AdvancedSearchMapParams,
  AdvancedSearchMapResponse,
  AdvancedSearchHeatmapResponse,
  AdvancedSearchMetricsResponse,
  UpdateAdvancedSearchParams,
} from './types';

export const advancedSearchesApi = createApi({
  reducerPath: 'advancedSearchesApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [ADVANCED_SEARCHES_TAG_TYPE],
  endpoints: (builder) => ({
    getAdvancedSearches: builder.query<
      AdvancedSearchListResponse,
      { pageSize?: number } | void
    >({
      query: (args) => {
        const pageSize = (args && 'pageSize' in args ? args.pageSize : undefined) ?? 50;
        const params = buildSearchParamsAuto({ page: 1, pageSize });
        return `v1/search/advanced-searches/${params ? '?' + params : ''}`;
      },
      providesTags: (result) => {
        const searches = result?.results || [];
        return [
          { type: ADVANCED_SEARCHES_TAG_TYPE, id: ALL_ADVANCED_SEARCHES_ID },
          ...searches.map(({ id }) => ({
            type: ADVANCED_SEARCHES_TAG_TYPE,
            id,
          })),
        ];
      },
    }),

    getAdvancedSearch: builder.query<AdvancedSearchDetail, number>({
      query: (id) => `v1/search/advanced-searches/${id}/`,
      providesTags: (_result, _error, id) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id },
      ],
    }),

    executeAdvancedSearch: builder.query<
      AdvancedSearchExecuteResponse,
      AdvancedSearchExecuteParams
    >({
      query: ({
        id,
        page,
        pageSize,
        sort,
        order,
        min_lat,
        max_lat,
        min_lng,
        max_lng,
        q,
        ...bodyParams
      }) => {
        const queryParams = buildSearchParamsAuto({
          page,
          pageSize,
          sort,
          order,
          min_lat,
          max_lat,
          min_lng,
          max_lng,
          q,
        });
        const queryString = queryParams.toString();
        return {
          url: `v1/search/advanced-searches/${id}/${queryString ? '?' + queryString : ''}`,
          method: 'POST',
          body: bodyParams,
        };
      },
      providesTags: (_result, _error, { id }) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: searchResultsTagId(id) },
      ],
    }),

    executeAdvancedSearchPaginated: builder.infiniteQuery<
      AdvancedSearchExecuteResponse,
      Omit<AdvancedSearchExecuteParams, 'page' | 'pageSize'> & {
        pageSize?: number;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return lastPageParam + 1;
        },
      },
      query({ queryArg, pageParam }) {
        const {
          id,
          pageSize = 50,
          sort,
          order,
          min_lat,
          max_lat,
          min_lng,
          max_lng,
          q,
          ...bodyParams
        } = queryArg;
        const queryParams = buildSearchParamsAuto({
          page: pageParam,
          pageSize,
          sort,
          order,
          min_lat,
          max_lat,
          min_lng,
          max_lng,
          q,
        });
        const queryString = queryParams.toString();
        return {
          url: `v1/search/advanced-searches/${id}/${queryString ? '?' + queryString : ''}`,
          method: 'POST',
          body: bodyParams,
        };
      },
      providesTags: (_result, _error, { id }) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: searchResultsTagId(id) },
      ],
    }),

    getAdvancedSearchMap: builder.query<
      AdvancedSearchMapResponse,
      AdvancedSearchMapParams
    >({
      query: ({
        id,
        aggregate_threshold,
        zoom,
        min_lat,
        max_lat,
        min_lng,
        max_lng,
        heatmap,
        metric,
        ...bodyParams
      }) => {
        const queryParams = buildSearchParamsAuto({
          aggregate_threshold,
          zoom,
          min_lat,
          max_lat,
          min_lng,
          max_lng,
          heatmap,
          metric,
        });
        const queryString = queryParams.toString();
        return {
          url: `v1/search/advanced-searches/${id}/map/${queryString ? '?' + queryString : ''}`,
          method: 'POST',
          body: bodyParams,
        };
      },
      providesTags: (_result, _error, { id }) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: searchResultsTagId(id) },
      ],
    }),

    getAdvancedSearchHeatmap: builder.query<
      AdvancedSearchHeatmapResponse,
      AdvancedSearchMapParams
    >({
      query: ({
        id,
        zoom,
        min_lat,
        max_lat,
        min_lng,
        max_lng,
        metric,
        ...bodyParams
      }) => {
        const queryParams = buildSearchParamsAuto({
          heatmap: true,
          zoom,
          min_lat,
          max_lat,
          min_lng,
          max_lng,
          metric,
        });
        const queryString = queryParams.toString();
        return {
          url: `v1/search/advanced-searches/${id}/map/${queryString ? '?' + queryString : ''}`,
          method: 'POST',
          body: bodyParams,
        };
      },
      providesTags: (_result, _error, { id }) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: searchResultsTagId(id) },
      ],
    }),

    getAdvancedSearchMetrics: builder.query<
      AdvancedSearchMetricsResponse,
      number
    >({
      query: (id) => `v1/search/advanced-searches/${id}/metrics/`,
      providesTags: (_result, _error, id) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: searchResultsTagId(id) },
      ],
    }),

    updateAdvancedSearch: builder.mutation<
      AdvancedSearchDetail,
      UpdateAdvancedSearchParams
    >({
      query: ({ id, data }) => ({
        url: `v1/search/advanced-searches/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id },
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: ALL_ADVANCED_SEARCHES_ID },
      ],
    }),

    deleteAdvancedSearch: builder.mutation<void, number>({
      query: (id) => ({
        url: `v1/search/advanced-searches/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: ADVANCED_SEARCHES_TAG_TYPE, id },
        { type: ADVANCED_SEARCHES_TAG_TYPE, id: ALL_ADVANCED_SEARCHES_ID },
      ],
    }),

    exportAdvancedSearch: builder.mutation<
      void,
      {
        searchId: number;
        body?: Record<string, any>;
        fileFormat: string;
        columns: string[];
        fileName: string;
      }
    >({
      query: ({ searchId, body, fileFormat, columns, fileName }) => {
        const url = `https://api.medscout.io/api/v1/search/advanced-searches/${searchId}/`;

        return {
          url: 'v1/admin/export/',
          method: 'POST',
          body: {
            method: 'POST',
            url,
            file_format: fileFormat,
            file_name: fileName,
            columns,
            body: body || {},
          },
        };
      },
    }),
  }),
});

export const {
  useGetAdvancedSearchesQuery,
  useGetAdvancedSearchQuery,
  useExecuteAdvancedSearchQuery,
  useExecuteAdvancedSearchPaginatedInfiniteQuery,
  useGetAdvancedSearchMapQuery,
  useGetAdvancedSearchHeatmapQuery,
  useGetAdvancedSearchMetricsQuery,
  useUpdateAdvancedSearchMutation,
  useDeleteAdvancedSearchMutation,
  useExportAdvancedSearchMutation,
} = advancedSearchesApi;
